import { addDisplayName, addProps, flowMax } from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { Link } from 'react-router-dom'
import { addAdminOrCreatorOrRedirect } from 'shared/src/api'
import { ReactComponent as RodeoLogo } from 'shared/src/assets/rodeo-logo.svg'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { makeShopperProfileUrl } from 'utils/routes'
import addResourceNames from '../utils/addResourceNames'
import Icon, { IconName } from './Icon'
import { IconButton } from './IconButton'
import { Modal } from './Modal'
import {
  BODY_MEDIUM_STYLE,
  MAIN_TITLE_STYLE,
  SANS_SERIF_FONT_FAMILY,
} from './styles'

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F9F4F1',
    paddingLeft: 24,
    paddingRight: 41,
    paddingTop: 38,
    paddingBottom: 24,
    overflowY: 'hidden',
  },
  topRowContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 40,
    height: 34,
  },
  closeButton: {
    marginRight: 14,
    marginLeft: -10,
  },
  logo: {
    marginRight: 'auto',
  },
  logoutButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontWeight: 900,
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: 1.6,
    color: 'rgba(54, 52, 50, 0.80)',
  },
  linksContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  navItemContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 32,
    '&:last-of-type': {
      marginBottom: 32,
    },
  },
  navItemLinkContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  navItemIcon: {
    marginRight: 24,
    marginTop: 3,
  },
  navItemRightColumnContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  navItemTitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 9,
  },
  navItemTitle: {
    ...MAIN_TITLE_STYLE,
    lineHeight: '34px',
    color: 'rgba(54, 52, 50, 0.80)',
    marginRight: 24,
  },
  navItemRightArrow: {
    color: 'rgba(54, 52, 50, 0.80)',
    marginTop: 11,
  },
  navItemDescription: {
    ...BODY_MEDIUM_STYLE,
    color: 'rgba(54, 52, 50, 0.56)',
    maxWidth: 232,
  },
  shopperAppLink: {
    background: '#363432',
    borderRadius: 16,
    width: 310,
    height: 56,
    alignSelf: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontWeight: 800,
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.09,
  },
})

interface NavItemProps {
  onClick: () => void
  to: string
  title: string
  description: string
  icon: IconName
}

const NavItem: FC<NavItemProps> = flowMax(
  addDisplayName('NavItem'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ onClick, to, icon, title, description, classes }) => (
    <li className={classes.navItemContainer}>
      <Link to={to} onClick={onClick} className={classes.navItemLinkContainer}>
        <Icon name={icon} className={classes.navItemIcon} />
        <span className={classes.navItemRightColumnContainer}>
          <span className={classes.navItemTitleContainer}>
            <span className={classes.navItemTitle}>{title}</span>
            <Icon name="chevron-right" className={classes.navItemRightArrow} />
          </span>
          <span className={classes.navItemDescription}>{description}</span>
        </span>
      </Link>
    </li>
  )
)

interface AdminNavItemProps {
  onClick: () => void
  to: string
}

const AdminNavItem: FC<AdminNavItemProps> = flowMax(
  addDisplayName('AdminNavItem'),
  ({ onClick, to, children }) => (
    <li>
      <Link to={to} onClick={onClick}>
        {children}
      </Link>
    </li>
  )
)

interface LogoutButtonProps {
  onClick: () => void
}

const LogoutButton: FC<LogoutButtonProps> = flowMax(
  addDisplayName('LogoutButton'),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({ onClick, classes, t }) => (
    <Link
      to="/logout"
      onClick={onClick}
      className={classes.logoutButtonContainer}
    >
      {t('admin.menu.logOut')}
    </Link>
  )
)

interface Props {
  onClose: () => void
}

export const Menu: FC<Props> = flowMax(
  addDisplayName('Menu'),
  addResourceNames,
  addAdminOrCreatorOrRedirect,
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({ onClose, user, resourceNames, classes, t }) => (
    <Modal containerClassName={classes.container}>
      <div className={classes.topRowContainer}>
        <IconButton
          name="dark-close"
          onClick={onClose}
          addHitSlop
          className={classes.closeButton}
        />
        <RodeoLogo
          style={{
            fill: 'white',
            width: 72,
            height: 72,
          }}
        ></RodeoLogo>
        <LogoutButton onClick={onClose} />
      </div>
      <ul className={classes.linksContainer}>
        {user.isAdmin ? (
          <>
            {resourceNames.map((name) => (
              <AdminNavItem to={`/${name}`} onClick={onClose} key={name}>
                {t(`admin.resources.${name}.namePlural`)}
              </AdminNavItem>
            ))}
            {user.isSuperAdmin && (
              <AdminNavItem to="/creatorEarnings" onClick={onClose}>
                {t('admin.earnings.title')}
              </AdminNavItem>
            )}
            <AdminNavItem to="/messages" onClick={onClose}>
              {t('admin.messages.title')}
            </AdminNavItem>
            <AdminNavItem to="/misc" onClick={onClose}>
              {t('admin.misc.title')}
            </AdminNavItem>
          </>
        ) : (
          <>
            <NavItem
              to="/"
              onClick={onClose}
              title={t('admin.menu.myRodeo')}
              description={t('admin.menu.myRodeoDescription')}
              icon="my-rodeo"
            />
            <NavItem
              to="/userLists"
              onClick={onClose}
              title={t('admin.menu.edits')}
              description={t('admin.menu.editsDescription')}
              icon="edits"
            />
            <NavItem
              to="/insights"
              onClick={onClose}
              title={t('admin.menu.insights')}
              description={t('admin.menu.insightsDescription')}
              icon="light-bulb"
            />
            {user.isCreator && !user.isExcludedFromCreatorFund && (
              <NavItem
                to="/earnings"
                onClick={onClose}
                title={t('admin.menu.creatorFund')}
                description={t('admin.menu.creatorFundDescription')}
                icon="creator-fund"
              />
            )}
            <NavItem
              to="/account"
              onClick={onClose}
              title={t('admin.menu.profile')}
              description={t('admin.menu.profileDescription')}
              icon="profile"
            />
          </>
        )}
      </ul>
      {!user.isAdmin && (
        <Link
          to={makeShopperProfileUrl(user.username ?? '')}
          className={classes.shopperAppLink}
          target="_blank"
        >
          {t('admin.menu.shopperAppLink')}
        </Link>
      )}
    </Modal>
  )
)
